import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Textarea } from "../components/ui/textarea";
import {
  ShieldCheck, ArrowLeft, ArrowRight, Loader2, Lock, Sparkles, Mail, Check,
  BarChart3, Shield, Flame, Phone, Headphones,
} from "lucide-react";
import { toast } from "sonner";

// Trial CTAs from the demo always land on the Haruki-branded /trial intake page
// (NOT the Emergent OAuth screen). Use a relative path — never hardcode origins.
const handleLogin = () => {
  window.location.href = "/trial";
};

const ZONE_META = {
  resilience: {
    label: "Resilience Zone",
    color: "hsl(var(--haruki-leaf))",
    blurb: "Your response showed grounded awareness, regulated attention, and active connection — the pattern HMDE associates with operational resilience.",
  },
  neutral: {
    label: "Neutral Zone",
    color: "hsl(var(--haruki-neutral))",
    blurb: "Your response was functional and steady. Many responders default here — Haruki training expands the toolkit so more responses land in resilience.",
  },
  fire: {
    label: "Emotional Fire Zone",
    color: "hsl(var(--haruki-fire))",
    blurb: "Your response showed elevated emotional charge. That's normal after the kind of call described — and exactly the signal Haruki training helps regulate over time.",
  },
};

const VERTICAL_CARDS = [
  { key: "law_enforcement", label: "Police", icon: Shield, blurb: "Patrol · Investigations · Specialty" },
  { key: "ems", label: "EMS", icon: Phone, blurb: "Paramedic · EMT · Mobile integrated health" },
  { key: "fire_service", label: "Fire Fighter", icon: Flame, blurb: "Suppression · Rescue · Hazmat" },
  { key: "dispatch_psap", label: "Public Safety", icon: Headphones, blurb: "Dispatch · PSAP · Communications" },
];

export default function DemoScenario() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [vertical, setVertical] = useState(params.get("vertical") || null);
  const [scenario, setScenario] = useState(null);
  const [phase, setPhase] = useState("pick"); // pick | loading | scenario | scoring | result
  const [own, setOwn] = useState("");
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!vertical) { setPhase("pick"); return; }
    setPhase("loading");
    api.get(`/demo/scenario?vertical=${vertical}`)
      .then((r) => { setScenario(r.data.scenario); setPhase("scenario"); })
      .catch(() => { toast.error("Could not load demo."); setPhase("pick"); });
  }, [vertical]);

  const submit = async (text) => {
    if (!text || text.trim().length < 2) {
      toast.error("Pick an option or write your own response.");
      return;
    }
    setPhase("scoring");
    try {
      const r = await api.post("/demo/respond", { response_text: text, vertical });
      setResult(r.data);
      setPhase("result");
    } catch (e) {
      toast.error("Scoring failed. Please try again.");
      setPhase("scenario");
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="border-b border-border bg-background/90 backdrop-blur">
        <div className="mx-auto flex max-w-4xl items-center justify-between px-6 py-4">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
            <ArrowLeft className="h-4 w-4" /> Back to site
          </Link>
          <div className="flex items-center gap-2">
            <div className="rounded-md p-1.5" style={{ background: "hsl(var(--haruki-leaf))" }}>
              <ShieldCheck className="h-4 w-4 text-white" />
            </div>
            <span className="font-heading text-base font-bold tracking-tight">Haruki Health</span>
          </div>
          <Button
            data-testid="demo-nav-trial-button"
            onClick={handleLogin}
            size="sm"
            style={{ background: "hsl(var(--haruki-leaf))", color: "white" }}
          >
            Start 21-day trial
          </Button>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-6 py-12 lg:py-16">
        <div className="mb-6 inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-bold uppercase tracking-[0.18em]"
             style={{ borderColor: "hsl(var(--haruki-leaf))", color: "hsl(var(--haruki-leaf-deep))", background: "hsl(var(--haruki-leaf) / 0.08)" }}>
          <Sparkles className="h-3.5 w-3.5" /> Anonymous demo · Resilience module · No data stored
        </div>

        <h1 className="font-heading text-3xl font-extrabold leading-tight sm:text-4xl">
          Take one scenario. Feel the engine.
        </h1>
        <p className="mt-3 max-w-xl text-sm text-muted-foreground">
          One resilience scenario, tailored to your discipline, scored by the same dual HMDE clinical engine your
          service members would use. Nothing you submit is stored or linked to you.
        </p>

        {/* VERTICAL PICKER */}
        {phase === "pick" && (
          <div className="mt-10 space-y-4 haruki-fade-in">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">
              Choose your context
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              {VERTICAL_CARDS.map((v) => (
                <button
                  key={v.key}
                  data-testid={`demo-vertical-${v.key}`}
                  onClick={() => setVertical(v.key)}
                  className="group flex items-center gap-4 rounded-lg border border-border bg-card p-5 text-left transition-all hover:border-foreground/30 hover:shadow-sm"
                >
                  <div className="rounded-md p-3 transition-transform group-hover:scale-105"
                       style={{ background: "hsl(var(--haruki-leaf) / 0.12)" }}>
                    <v.icon className="h-5 w-5" style={{ color: "hsl(var(--haruki-leaf-deep))" }} />
                  </div>
                  <div className="flex-1">
                    <div className="font-heading text-base font-bold">{v.label}</div>
                    <div className="text-xs text-muted-foreground">{v.blurb}</div>
                  </div>
                  <ArrowRight className="h-4 w-4 text-muted-foreground transition-transform group-hover:translate-x-1" />
                </button>
              ))}
            </div>
            <div className="pt-6 text-center">
              <Link to="/demo/sample-report"
                    data-testid="demo-sample-report-button-pick"
                    className="inline-flex items-center gap-2 rounded-md border-2 px-4 py-2 text-sm font-semibold transition-colors hover:bg-card"
                    style={{ borderColor: "hsl(var(--haruki-teal))", color: "hsl(var(--haruki-teal))" }}>
                <BarChart3 className="h-4 w-4" />
                Show Sample Report
              </Link>
              <div className="mt-2 text-xs text-muted-foreground">
                For HR · training directors · command staff — see exactly what the agency console shows.
              </div>
            </div>
          </div>
        )}

        {phase === "loading" && (
          <div className="mt-10 flex h-48 items-center justify-center text-muted-foreground">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading scenario…
          </div>
        )}

        {scenario && (phase === "scenario" || phase === "scoring") && (
          <div className="mt-10 space-y-6 haruki-fade-in">
            <Card>
              <CardContent className="p-6 lg:p-8">
                <div className="mb-3 flex items-center justify-between">
                  <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground">
                    Scenario · {VERTICAL_CARDS.find(v => v.key === vertical)?.label}
                  </span>
                  <span className="rounded-full px-2 py-0.5 text-[10px] font-semibold"
                        style={{ background: "hsl(var(--haruki-leaf) / 0.15)", color: "hsl(var(--haruki-leaf-deep))" }}>
                    {scenario.modality_primary?.replace(/_/g, " ")} modality
                  </span>
                </div>
                <p data-testid="demo-scenario-text" className="text-base leading-relaxed">{scenario.scenario_text}</p>
                <div className="mt-4 flex flex-wrap gap-3 text-xs text-muted-foreground">
                  <span>Avatar: {scenario.avatar_pairing.replace(/_/g, " ")}</span>
                  <span>·</span>
                  <span>Setting: {scenario.setting.replace(/_/g, " ")}</span>
                </div>
                <button onClick={() => { setVertical(null); setScenario(null); setResult(null); setPhase("pick"); }}
                        data-testid="demo-change-vertical-button"
                        className="mt-4 text-xs underline-offset-4 hover:underline text-muted-foreground">
                  Change context
                </button>
              </CardContent>
            </Card>

            <div className="space-y-2">
              <div className="text-xs uppercase tracking-widest text-muted-foreground">Choose a response</div>
              {scenario.response_options.map((opt, i) => (
                <button
                  key={opt.id}
                  data-testid={`demo-option-${i}`}
                  onClick={() => submit(opt.text)}
                  disabled={phase === "scoring"}
                  className="block w-full rounded-md border border-border bg-card px-4 py-3 text-left text-sm transition-all hover:border-foreground/40 hover:shadow-sm disabled:opacity-50"
                >
                  <span className="text-foreground">{opt.text}</span>
                  <span className="ml-2 text-xs text-muted-foreground">— {opt.modality?.replace(/_/g, " ")}</span>
                </button>
              ))}
            </div>

            <Card>
              <CardContent className="space-y-3 p-6">
                <div className="text-xs uppercase tracking-widest text-muted-foreground">…or respond in your own words</div>
                <Textarea
                  data-testid="demo-own-textarea"
                  value={own}
                  onChange={(e) => setOwn(e.target.value)}
                  placeholder="Type how you'd respond."
                  className="min-h-[90px]"
                />
                <Button
                  data-testid="demo-own-submit"
                  onClick={() => submit(own)}
                  disabled={phase === "scoring"}
                  style={{ background: "hsl(var(--haruki-leaf))", color: "white" }}
                >
                  {phase === "scoring"
                    ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Scoring…</>
                    : "Score my response"}
                </Button>
              </CardContent>
            </Card>
          </div>
        )}

        {phase === "result" && result && (
          <div className="mt-10 space-y-6 haruki-fade-in">
            <div
              data-testid="demo-zone-result"
              className="rounded-lg border-2 p-6"
              style={{ borderColor: ZONE_META[result.zone]?.color }}
            >
              <div className="text-xs uppercase tracking-widest text-muted-foreground">Your response landed in the</div>
              <div className="mt-1 font-heading text-3xl font-extrabold" style={{ color: ZONE_META[result.zone]?.color }}>
                {ZONE_META[result.zone]?.label}
              </div>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{ZONE_META[result.zone]?.blurb}</p>
            </div>

            <div className="rounded-md border border-border p-6" style={{ background: "hsl(var(--card))" }}>
              <div className="text-xs uppercase tracking-widest text-muted-foreground">Reflection prompt</div>
              <p className="mt-2 text-base leading-relaxed">{result.reflection_prompt}</p>
            </div>

            <div
              className="rounded-xl border p-8 text-center"
              style={{ background: "hsl(var(--haruki-leaf-deep))", color: "white" }}
            >
              <Lock className="mx-auto h-6 w-6" style={{ color: "hsl(var(--haruki-leaf))" }} />
              <h2 className="mt-3 font-heading text-2xl font-bold">That was 1 of 40+ scenarios across 8 verticals.</h2>
              <p className="mx-auto mt-3 max-w-xl text-sm text-white/80">
                Your trial unlocks the full library, all four training modules, agency dashboards, CEU certificates,
                and the engagement dashboard your training calendar deserves.
              </p>
              <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
                <Button
                  data-testid="demo-cta-trial-button"
                  onClick={handleLogin}
                  size="lg"
                  style={{ background: "hsl(var(--haruki-leaf))", color: "white" }}
                >
                  Start your 21-day trial <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
                <Link
                  to="/demo/sample-report"
                  data-testid="demo-sample-report-button"
                  className="inline-flex items-center gap-2 rounded-md border-2 px-4 py-3 text-sm font-semibold transition-colors hover:bg-white/5"
                  style={{ borderColor: "hsl(var(--haruki-leaf))", color: "white" }}
                >
                  <BarChart3 className="h-4 w-4" /> Show Sample Report
                </Link>
                <button
                  data-testid="demo-try-another-button"
                  onClick={() => { setResult(null); setOwn(""); setPhase("scenario"); }}
                  className="text-sm font-semibold text-white/70 underline-offset-4 hover:text-white hover:underline"
                >
                  Try a different response
                </button>
              </div>
              <div className="mt-4 text-xs text-white/60">
                No card required. Your demo response is anonymous — we do not store it.
              </div>
              <LeadCapture />
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

const LeadCapture = () => {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setError(null);
    setSubmitting(true);
    try {
      await api.post("/demo/lead", { email, name: name || null, source: "demo_signup_wall" });
      setDone(true);
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not save. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div
        data-testid="lead-capture-success"
        className="mt-6 inline-flex items-center gap-2 rounded-md border px-4 py-2 text-sm"
        style={{ borderColor: "hsl(var(--haruki-leaf))", color: "white", background: "hsl(var(--haruki-leaf) / 0.18)" }}
      >
        <Check className="h-4 w-4" style={{ color: "hsl(var(--haruki-leaf))" }} />
        Thanks — we'll send you trial materials and a link.
      </div>
    );
  }

  return (
    <div className="mt-8 border-t border-white/15 pt-6">
      <div className="mb-3 text-xs font-semibold uppercase tracking-[0.18em] text-white/70">
        Not ready to start the trial yet?
      </div>
      <div className="mb-4 text-sm text-white/80">
        Drop your email — we'll send a short briefing and a trial link you can use whenever your calendar opens up.
      </div>
      <form onSubmit={submit} className="mx-auto flex max-w-lg flex-col gap-2 sm:flex-row">
        <input
          data-testid="lead-name-input" type="text" value={name} onChange={(e) => setName(e.target.value)}
          placeholder="Your name (optional)"
          className="flex-1 rounded-md border border-white/20 bg-white/10 px-3 py-2 text-sm text-white placeholder:text-white/50 outline-none focus:border-white/50"
        />
        <input
          data-testid="lead-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
          placeholder="you@agency.gov"
          className="flex-[1.5] rounded-md border border-white/20 bg-white/10 px-3 py-2 text-sm text-white placeholder:text-white/50 outline-none focus:border-white/50"
        />
        <button
          data-testid="lead-submit-button" type="submit" disabled={submitting || !email.trim()}
          className="inline-flex items-center justify-center gap-2 rounded-md px-4 py-2 text-sm font-semibold transition-opacity hover:opacity-90 disabled:opacity-50"
          style={{ background: "hsl(var(--haruki-leaf))", color: "white" }}
        >
          {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
          Send me the briefing
        </button>
      </form>
      {error && (
        <div data-testid="lead-capture-error" className="mt-3 text-xs" style={{ color: "hsl(var(--haruki-fire))" }}>
          {error}
        </div>
      )}
      <div className="mt-2 text-[10px] uppercase tracking-widest text-white/40">
        No spam. Confidential. One briefing email + an optional trial link.
      </div>
    </div>
  );
};
